import React, { useState } from "react";
import SEO from "@/components/SEO";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { BokehParticles } from "@/components/effects/BokehParticles";
import { LightStreaks } from "@/components/effects/LightStreaks";
import { Reveal } from "@/components/effects/Reveal";
import { useAdminAuth } from "@/admin/AuthContext";
import { formatErr } from "@/admin/api";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, admin.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-admin-login" className="relative min-h-[100svh] overflow-hidden flex items-center justify-center py-16">
      <SEO title="AdminLogin · WelkomBiz Photography" noindex />
      <div className="absolute inset-0 -z-10" style={{
        background:
          "radial-gradient(70% 60% at 50% 0%, rgba(255,46,147,0.12) 0%, transparent 60%)," +
          "radial-gradient(70% 60% at 50% 100%, rgba(24,211,255,0.10) 0%, transparent 60%)," +
          "linear-gradient(180deg, #000, #050507 50%, #000)",
      }} />
      <div className="aperture-ring absolute inset-0 m-auto w-[60vmin] h-[60vmin] opacity-20" />
      <LightStreaks count={4} />
      <BokehParticles count={12} />

      <div className="relative w-full max-w-[480px] mx-auto px-6">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-8">
            <Logo size={68} linkTo={null} />
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass chrome-border">
              <ShieldCheck className="w-3.5 h-3.5 text-[#ff2e93]" />
              <span className="text-[10px] tracking-luxe uppercase text-white/70">Admin Access Only</span>
            </div>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl">
              <span className="text-chrome">WelkomBiz Photography</span>
              <br />
              <span className="italic text-magcyan">Admin Portal</span>
            </h1>
            <p className="mt-4 text-sm text-white/55 font-body max-w-sm">
              Secure access for managing events, galleries, customer enquiries and platform content.
            </p>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <form onSubmit={submit} data-testid="admin-login-form" className="glass-strong chrome-border rounded-md p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,46,147,0.18), transparent 70%)" }} />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(24,211,255,0.16), transparent 70%)" }} />

            <div className="relative space-y-4">
              <label className="block">
                <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
                  <Mail className="w-3 h-3" /> Admin Email
                </span>
                <input
                  data-testid="admin-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@welkombiz.co.za"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]"
                />
              </label>
              <label className="block">
                <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
                  <Lock className="w-3 h-3" /> Password
                </span>
                <input
                  data-testid="admin-login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 outline-none font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)]"
                />
              </label>

              <button type="submit" data-testid="admin-login-submit" disabled={busy} className="btn-primary w-full mt-2 disabled:opacity-60">
                {busy ? "Authenticating…" : "Sign In"}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <div className="text-center mt-1">
                <a href="/forgot-password?role=admin" data-testid="admin-login-forgot" className="text-[11px] tracking-luxe uppercase text-white/55 hover:text-white">
                  Forgot password?
                </a>
              </div>
            </div>

            <div className="relative mt-6 flex items-center gap-3 px-3 py-2.5 rounded-md border border-white/8 bg-white/5">
              <Sparkles className="w-3 h-3 text-[#18d3ff]" />
              <p className="text-[11px] text-white/60 font-body">
                Sessions remain active for 12 hours · Brute force protection active.
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
